import {inject, Injectable} from '@angular/core';
import {BUTTON_COLOR} from '@discocil/fv-components';
import {Observable, of, timer} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {PaymentInteractionHandler} from './payment-interaction.handler';
import {PaymentFeedbackPresenter} from './payment-feedback.presenter';
import {PaymentVerificationRecovery} from '../../domain/ports/ui/recovery.port';
import {Payment} from '../../domain/entities/payment.entity';
import {PaymentPollingFailedError} from '../../domain/model/payment.errors';

enum PendingPaymentChoice {
    RESUME,
    DISMISS,
}

@Injectable({providedIn: 'root'})
export class VerificationRecoveryHandler implements PaymentVerificationRecovery {
    private readonly interaction = inject(PaymentInteractionHandler);
    private readonly feedback = inject(PaymentFeedbackPresenter);

    recoverVerification(payment: Payment, error: unknown): Observable<boolean> {
        if (error instanceof PaymentPollingFailedError) {
            return of(false);
        }

        return this.askToResumePolling({ totalAmount: payment.amount });
    }

    private askToResumePolling(params: { totalAmount: number }, manageLoading = true): Observable<boolean> {
        if (manageLoading) {
            this.feedback.hideLoading();
        }

        return timer(300).pipe(
            switchMap(() => this.askForResume(params)),
            tap(choice => this.handlePollingChoice(choice, manageLoading)),
            map(choice => choice === PendingPaymentChoice.RESUME)
        );
    }

    private handlePollingChoice(choice: PendingPaymentChoice, manageLoading: boolean): void {
        if (manageLoading && choice === PendingPaymentChoice.RESUME) {
            this.feedback.showLoading();
        }
    }

    private askForResume(params: { totalAmount: number }): Observable<PendingPaymentChoice> {
        return this.interaction.askUserChoice<PendingPaymentChoice>({
            titleKey: 'fv_confirm.payment.error_checking_status_title',
            descriptionKey: 'fv_confirm.payment.error_checking_status_message',
            descriptionParams: { amount: params.totalAmount },
            defaultValue: PendingPaymentChoice.DISMISS,
            options: [
                {
                    labelKey: 'fv_confirm.payment.error_checking_status_resume',
                    value: PendingPaymentChoice.RESUME,
                    style: { color: BUTTON_COLOR.primary }
                },
                {
                    labelKey: 'global.cancel',
                    value: PendingPaymentChoice.DISMISS,
                    style: { color: BUTTON_COLOR.transparent }
                },
            ],
        });
    }
}
